import api from '@/client/axiosInstance';

// TCX-814 Universal Referral Terms API client. Single source of truth for all
// URT network calls; the 811/810/739 UI imports these (never re-defines them).
// PDF endpoints return { file: base64, filename } — feed to downloadBase64Pdf.

export type UrtStatus = 'draft' | 'published' | 'archived';

export interface UrtVersionSummary {
  version: number;
  signerName: string | null;
  signedAt: string | null;
  isCurrent: boolean;
}

export interface UrtCurrent {
  version: number;
  contentHtml: string;
  updatedAt: string | null;
  title: string;
}

// The franchisor setup payload's `current` carries the full published body so
// the setup page can seed the editor from the live terms (not the scaffold seed).
export interface UrtCurrentForFranchise {
  version: number;
  bodyText: string;
  signerName: string | null;
  signedAt: string | null;
  publishedAt: string | null;
  contentHtml: string;
}

export interface UrtForFranchise {
  enabled: boolean;
  current: UrtCurrentForFranchise | null;
  draft: {
    bodyText: string;
    signerName: string | null;
    updatedAt?: string | null;
  } | null;
  totalVersions: number;
}

export interface UrtPdfResponse {
  file: string;
  filename: string;
}

// ── Franchisor (authed) ────────────────────────────────────────────────
export const getMyReferralTerms = async (): Promise<UrtForFranchise> => {
  const { data } = await api.get('universal-referral-terms/me');
  return data;
};

export const saveReferralTermsDraft = async (body: {
  bodyText: string;
  signerName: string;
}): Promise<void> => {
  await api.post('universal-referral-terms/draft', body);
};

export const publishReferralTerms = async (body: {
  bodyText: string;
  signerName: string;
}): Promise<UrtVersionSummary> => {
  const { data } = await api.post('universal-referral-terms/publish', body);
  return data;
};

export const editAndRenewReferralTerms = async (): Promise<void> => {
  await api.post('universal-referral-terms/edit-renew');
};

export const downloadMyReferralTermsPdf = async (
  version: number,
): Promise<UrtPdfResponse> => {
  const { data } = await api.get(
    `universal-referral-terms/versions/${version}/pdf`,
  );
  return data;
};

// Download the saved-but-unpublished draft (TCX-811 "Save for Later"). The
// draft has no version, so this hits a dedicated endpoint that composes the
// agreement PDF from the draft's current content.
export const downloadMyReferralTermsDraftPdf =
  async (): Promise<UrtPdfResponse> => {
    const { data } = await api.get('universal-referral-terms/draft/pdf');
    return data;
  };

// ── Public (franchisor profile) ────────────────────────────────────────
export const getPublicReferralTerms = async (
  franchiseId: string,
): Promise<UrtCurrent | null> => {
  const { data } = await api.get(
    `public/universal-referral-terms/${franchiseId}`,
  );
  return data ?? null;
};

export const downloadPublicReferralTermsPdf = async (
  franchiseId: string,
): Promise<UrtPdfResponse> => {
  const { data } = await api.get(
    `public/universal-referral-terms/${franchiseId}/pdf`,
  );
  return data;
};

// ── Admin (franchise details) ──────────────────────────────────────────
export const getAdminReferralTermsHistory = async (
  franchiseId: string,
): Promise<UrtForFranchise & { versions: UrtVersionSummary[] }> => {
  const { data } = await api.get(
    `admin/franchises/${franchiseId}/universal-referral-terms`,
  );
  return data;
};

export const downloadAdminReferralTermsPdf = async (
  franchiseId: string,
  version: number,
): Promise<UrtPdfResponse> => {
  const { data } = await api.get(
    `admin/franchises/${franchiseId}/universal-referral-terms/${version}/pdf`,
  );
  return data;
};

// ── Consultant (franchisor profile, TCX-1070) ──────────────────────────
export interface ConsultantUrtAcceptance {
  firstName: string;
  lastName: string;
  acceptedAt: string;
}

export interface ConsultantUrtState {
  // contentHtml already carries both signature lines, composed server-side.
  terms: UrtCurrent | null;
  // Only the caller's acceptance of the CURRENT version; null = pending.
  acceptance: ConsultantUrtAcceptance | null;
}

export interface AcceptReferralTermsBody {
  version: number;
  firstName: string;
  lastName: string;
  timezone?: string;
}

export const getConsultantReferralTerms = async (
  franchiseId: string,
): Promise<ConsultantUrtState> => {
  const { data } = await api.get(
    `consultant/universal-referral-terms/${franchiseId}`,
  );
  return data;
};

export const acceptReferralTerms = async (
  franchiseId: string,
  body: AcceptReferralTermsBody,
): Promise<ConsultantUrtState> => {
  const { data } = await api.post(
    `consultant/universal-referral-terms/${franchiseId}/accept`,
    body,
  );
  return data;
};

export const downloadConsultantReferralTermsPdf = async (
  franchiseId: string,
): Promise<UrtPdfResponse> => {
  const { data } = await api.get(
    `consultant/universal-referral-terms/${franchiseId}/pdf`,
  );
  return data;
};
